import {
  MEMBER_INVITE_LIST_LIMIT,
  type MemberInviteRow,
} from "./member-invites";
import { MEMBER_LIMIT, type MemberRow } from "./members";

const INVITE_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DISPLAY_EMAIL_LIKE_PATTERN =
  /[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+/giu;

export interface PlatformMemberSearchHit {
  readonly kind: "member";
  /** Opaque React key. Never render this value. */
  readonly key: string;
  readonly displayName: string;
  readonly email: string | null;
  readonly role: string;
  readonly status: "active" | "suspended";
  readonly createdAt: string | null;
}

export interface PlatformInviteSearchHit {
  readonly kind: "invite";
  /** Opaque React key. Never render this value. */
  readonly key: string;
  /** In-memory dispatch to the shipped invite drawer only. Never render or serialize. */
  readonly inviteId: string;
  readonly displayName: string;
  readonly email: string;
  readonly role: string;
  readonly status: "pending";
  readonly expiresAt: string | null;
  readonly createdAt: string | null;
  readonly updatedAt: string | null;
}

export type PlatformPersonSearchHit = PlatformMemberSearchHit | PlatformInviteSearchHit;

export function normalizePlatformPeopleSearchQuery(query: string): string {
  return query.trim().toLowerCase();
}

/** A normalized address is used only to prefill the existing Send invite form. */
export function normalizeInviteEmailQuery(query: string): string | null {
  const normalized = normalizePlatformPeopleSearchQuery(query);
  return INVITE_EMAIL_PATTERN.test(normalized) ? normalized : null;
}

export function redactPlatformPeopleEmail(email: string | null): string {
  const value = email?.trim() ?? "";
  if (value.length === 0) return "Not provided";
  const separator = value.indexOf("@");
  if (separator <= 0 || separator === value.length - 1) return "•••";
  return `${value.slice(0, 1)}•••${value.slice(separator)}`;
}

export function platformPeopleDisplayName(
  value: string | null,
  opaqueId: string,
  fallback: string,
): string {
  const trimmed = value?.trim() ?? "";
  if (trimmed.length === 0) return fallback;
  if (opaqueId.length > 0 && trimmed.toLowerCase().includes(opaqueId.toLowerCase())) {
    return fallback;
  }
  return trimmed.replace(DISPLAY_EMAIL_LIKE_PATTERN, (email) => redactPlatformPeopleEmail(email));
}

function matchesPerson(
  displayName: string | null,
  email: string | null,
  needle: string,
): boolean {
  return (
    normalizePlatformPeopleSearchQuery(displayName ?? "").includes(needle) ||
    normalizePlatformPeopleSearchQuery(email ?? "").includes(needle)
  );
}

export function searchPlatformPeople(
  members: readonly MemberRow[],
  invites: readonly MemberInviteRow[],
  query: string,
): PlatformPersonSearchHit[] {
  const needle = normalizePlatformPeopleSearchQuery(query);
  if (needle.length === 0) return [];

  const hits: PlatformPersonSearchHit[] = [];

  members.forEach((member, index) => {
    if (member.status !== "active" && member.status !== "suspended") return;
    if (!matchesPerson(member.memberDisplayName, member.memberEmail, needle)) return;

    hits.push({
      kind: "member",
      key: member.membershipId.length > 0 ? `member:${member.membershipId}` : `member-row:${index}`,
      displayName: platformPeopleDisplayName(
        member.memberDisplayName,
        member.membershipId,
        "Member",
      ),
      email: member.memberEmail,
      role: member.role,
      status: member.status,
      createdAt: member.createdAt,
    });
  });

  invites.forEach((invite, index) => {
    if (invite.status !== "pending") return;
    if (!matchesPerson(invite.displayName, invite.email, needle)) return;

    hits.push({
      kind: "invite",
      key: invite.inviteId.length > 0 ? `invite:${invite.inviteId}` : `invite-row:${index}`,
      inviteId: invite.inviteId,
      displayName: platformPeopleDisplayName(
        invite.displayName,
        invite.inviteId,
        "Invited member",
      ),
      email: invite.email,
      role: invite.role,
      status: "pending",
      expiresAt: invite.expiresAt,
      createdAt: invite.createdAt,
      updatedAt: invite.updatedAt,
    });
  });

  return hits;
}

export function isPlatformPeopleSearchCapped(
  members: readonly MemberRow[],
  invites: readonly MemberInviteRow[],
): boolean {
  return members.length >= MEMBER_LIMIT || invites.length >= MEMBER_INVITE_LIST_LIMIT;
}
